import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Cloud, Users, TestTube2, CreditCard, ClipboardList,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';
import { ServiceCardLarge } from '../components/shared/ServiceCard';

const serviceCategories = [
  {
    title: 'Core Platforms',
    description: 'Implementation and optimization of Guidewire core systems.',
    color: 'bg-sky-500',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
    services: [
      {
        icon: Shield,
        title: 'PolicyCenter Services',
        description: 'End-to-end policy administration with custom product modeling, rating engines, and seamless API integrations for modern insurance operations.',
        features: ['Configuration & Product Modeling', 'Business Rule Design', 'Rating Engines', 'API Integrations'],
        href: '/services/policycenter',
      },
      {
        icon: ClipboardList,
        title: 'ClaimCenter Services',
        description: 'Streamlined claims processing with intelligent workflows, automation, and third-party integrations for faster claim resolution.',
        features: ['Workflow Design', 'Automation & Smart Decisions', 'Third Party Integrations', 'Reporting & Analytics'],
        href: '/services/claimcenter',
      },
      {
        icon: CreditCard,
        title: 'BillingCenter Services',
        description: 'Modern billing solutions with payment gateway integrations, flexible installment plans, and comprehensive billing automation.',
        features: ['Custom Billing Flows', 'Payment Gateway Integrations', 'Installment Plans', 'Subscription Billing'],
        href: '/services/billingcenter',
      },
    ],
  },
  {
    title: 'Cloud & Modernisation',
    description: 'Transform your infrastructure for the cloud-first era.',
    color: 'bg-emerald-500',
    image: 'https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?w=800&h=400&fit=crop',
    services: [
      {
        icon: Cloud,
        title: 'Cloud Migration & Modernisation',
        description: 'Seamless migration to Guidewire Cloud with optimized architecture, zero-downtime deployment, and performance tuning.',
        features: ['Cloud Strategy & Architecture', 'Data Migration', 'CI/CD Implementation', 'Performance Optimization'],
        href: '/cloud-modernisation',
      },
    ],
  },
  {
    title: 'QA & Staffing',
    description: 'Quality assurance and talent solutions for your teams.',
    color: 'bg-violet-500',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop',
    services: [
      {
        icon: TestTube2,
        title: 'QA & Testing Services',
        description: 'Comprehensive testing services including functional, regression, performance, and security testing for Guidewire platforms.',
        features: ['Functional Testing', 'Regression Automation', 'Performance Testing', 'Security Testing'],
        href: '/qa-testing',
      },
      {
        icon: Users,
        title: 'Staffing & Talent',
        description: 'Access certified Guidewire professionals for contract, project, or dedicated team engagements with flexible models.',
        features: ['Contract Consultants', 'Dedicated Teams', 'Project Managers', 'Training & Enablement'],
        href: '/staffing',
      },
    ],
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      <SEO {...pageSEO.services} />
      <PageHeader
        label="Our Services"
        title="Guidewire Services for the Modern Insurer"
        description="Comprehensive solutions across Policy, Claims, Billing, Cloud & QA to accelerate your digital transformation and maximize ROI."
      />

      {/* Service Categories */}
      {serviceCategories.map((category, categoryIndex) => (
        <section 
          key={categoryIndex}
          className={`py-20 lg:py-24 ${categoryIndex % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800'}`}
          data-testid={`service-category-${categoryIndex}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Header with Image */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <AnimatedSection className="lg:col-span-2">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-1 h-8 ${category.color} rounded-full`} />
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                    {category.title}
                  </h2>
                </div>
                <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl">
                  {category.description}
                </p>
              </AnimatedSection>
              
              <AnimatedSection direction="right" className="hidden lg:block">
                <motion.div
                  className="relative overflow-hidden rounded-2xl h-32"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={category.image} 
                    alt={category.title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-r ${categoryIndex === 0 ? 'from-sky-600/20' : categoryIndex === 1 ? 'from-emerald-600/20' : 'from-violet-600/20'} to-transparent`} />
                </motion.div>
              </AnimatedSection>
            </div>

            <StaggerContainer className={`grid grid-cols-1 ${category.services.length === 1 ? 'lg:grid-cols-1 max-w-3xl' : category.services.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-8`}>
              {category.services.map((service, serviceIndex) => (
                <StaggerItem key={serviceIndex}>
                  <ServiceCardLarge {...service} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-sky-600 to-sky-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-lg text-sky-100 max-w-2xl mx-auto mb-8">
              Our experts can help you assess your current state and recommend the 
              right services to achieve your business objectives.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-white text-sky-700 hover:bg-sky-50 h-14 px-8"
              >
                Schedule a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Services;
